import { useMemo } from "react";
import moment from "moment-timezone";
import { CalendarPlus, Clock, MessageCircle } from "lucide-react";
import { AppPageHeader } from "@shared/ui/AppPageHeader";
import { Avatar } from "@shared/ui/Avatar";
import { Button } from "@shared/ui/Button";
import { Loader } from "@shared/ui/Loader";
import { TimezoneOption } from "@platform/features/timezones/timezones.types";
import { getDSTAwareAbbreviationFromTimezone } from "@platform/features/timezones/utils/getDSTAwareAbbreviationFromTimezone";
import { useCircleTimeslot } from "@platform/features/program/hooks/useCircleTimeslot";
import { ChatButton } from "@platform/features/chat/components/ChatButton/ChatButton";
import { useChatVisibility } from "@platform/features/chat/hooks/useChatVisibility";
import { Circle } from "@platform/types/platform.types";

export type Props = {
  circle?: Circle;
  isLoading: boolean;
  isAddToCalendarDisabled: boolean;
  isChatDisabled: boolean;
  onOpenCircleDialog: () => void;
  onOpenAddCalendarEventsDialog: () => void;
  userTimezone?: TimezoneOption | undefined;
  scheduledEngagementStartDate?: string;
};

export function ProgramCircle({
  userTimezone,
  onOpenCircleDialog,
  onOpenAddCalendarEventsDialog,
  isLoading,
  isAddToCalendarDisabled,
  isChatDisabled,
  circle,
  scheduledEngagementStartDate,
}: Props) {
  const { startTimeslot } = useCircleTimeslot();
  const { shouldShowChat } = useChatVisibility(isChatDisabled);

  const headerDescription = useMemo(() => {
    if (isLoading) {
      return undefined;
    }

    if (!startTimeslot) {
      return undefined;
    }

    if (!scheduledEngagementStartDate) {
      return undefined;
    }

    if (!userTimezone) {
      return undefined;
    }

    return `Meeting on five ${startTimeslot?.local.start.day}s at ${
      startTimeslot?.local.start.formattedTime
    }${
      userTimezone?.abbreviation
        ? ` ${getDSTAwareAbbreviationFromTimezone(
            userTimezone,
            startTimeslot?.local.start.dateString
          )}`
        : ""
    }, starting the week of ${moment(scheduledEngagementStartDate).format(
      "MMM D"
    )}.`;
  }, [isLoading, startTimeslot, userTimezone, scheduledEngagementStartDate]);

  return (
    <div className="flex flex-col gap-8 w-full border border-brand-800 rounded-lg px-6 py-6 mt-8 md:mt-0">
      <div className="flex flex-col gap-6 justify-between items-start w-full md:flex-row">
        <AppPageHeader
          title="Your Peer Circle"
          description={isLoading ? undefined : headerDescription}
          iconComponent={isLoading ? undefined : Clock}
        />

        {circle && (!isAddToCalendarDisabled || !isChatDisabled) && (
          <div className="flex flex-row gap-2">
            {!isAddToCalendarDisabled && circle.meetings?.length > 0 && (
              <Button
                variant="secondary"
                iconComponent={CalendarPlus}
                onClick={onOpenAddCalendarEventsDialog}
              >
                Add to Calendar
              </Button>
            )}

            {shouldShowChat && (
              <ChatButton variant="primary">Circle Chat</ChatButton>
            )}
          </div>
        )}
      </div>

      <div className="flex flex-row gap-2">
        {circle ? (
          <div className="flex flex-col gap-4 items-start md:flex-row">
            <div className="flex flex-row gap-2" onClick={onOpenCircleDialog}>
              {circle.users.map((user) => (
                <Avatar
                  key={user.id}
                  image={user.profile_photo}
                  size={36}
                  alt={`${user.first_name} ${user.last_name}`}
                />
              ))}
            </div>
            <Button
              variant="link-inline"
              className="underline md:no-underline"
              onClick={onOpenCircleDialog}
            >
              View More
            </Button>
          </div>
        ) : (
          <Loader />
        )}
      </div>
    </div>
  );
}

export default ProgramCircle;
